import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input as routerInput } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import {
  DatatableColumn,
  DatatableSorting,
  FetchData,
  RktTableComponent,
  RktTableSearchComponent,
} from '@rocketfinancialcorp/rocket-ui/table';
import { FilterField, RequestPageParams, FilterValues } from '@shared/models';
import { financialAccountFeature, FinancialAcccountActions } from '@store';
import { isUndefined, pickBy, identity } from 'lodash-es';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { FilterMenuComponent } from '@shared/components/filters';

@Component({
  selector: 'app-child-financial-accounts',
  standalone: true,
  imports: [AsyncPipe, RktIconComponent, RktButtonDirective, FilterMenuComponent, RktTableSearchComponent, RktTableComponent],
  templateUrl: './child-financial-accounts.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChildFinancialAccountsComponent {
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  store = inject(Store);

  financialAccountId = routerInput<string>();
  searchString?: string;

  columns: DatatableColumn[] = [
    { name: 'FA ID', prop: 'financialAccountShortId', flexSize: 170, colType: 'short-id' },
    { name: 'BA ID', prop: 'businessAccountShortId', flexSize: 170, colType: 'short-id' },
    { name: 'Account Name', prop: 'name', flexSize: 330 },
    { name: 'Account Number', prop: 'displayName', flexSize: 250 },
    { name: 'Category', prop: 'category', flexSize: 180, isSortable: true },
    { name: 'Account Balance', prop: 'accountBalance', flexSize: 180, colType: 'amount', colAlign: 'right' },
    { name: 'Available Balance', prop: 'availableBalance', flexSize: 180, colType: 'amount', colAlign: 'right' },
    { name: 'Status', prop: 'state', flexSize: 140, colType: 'status-tag', colAlign: 'right', isSortable: true },
  ];

  filters: FilterField[] = [
    {
      name: 'categories',
      displayName: 'Category',
      type: 'RADIO',
      options: [
        { label: 'EXTERNAL', value: 'EXTERNAL' },
        { label: 'INTERNAL', value: 'INTERNAL' },
        { label: 'INTEGRATED', value: 'INTEGRATED' },
      ],
    },
    {
      name: 'type',
      displayName: 'Account Type',
      type: 'RADIO',
      options: [
        { label: 'BANK', value: 'BANK' },
        { label: 'CARD', value: 'CARD' },
      ],
    },
    {
      name: 'subtype',
      displayName: 'Account Sub Type',
      type: 'RADIO',
      options: [
        { label: 'CHECKING', value: 'CHECKING' },
        { label: 'SAVING', value: 'SAVING' },
        { label: 'DEBIT', value: 'DEBIT' },
        { label: 'CREDIT', value: 'CREDIT' },
        { label: 'TOKENIZED', value: 'TOKENIZED' },
      ],
    },
    {
      name: 'state',
      displayName: 'Status',
      type: 'RADIO',
      options: [
        { label: 'Active', value: 'ACTIVE' },
        { label: 'Suspended', value: 'SUSPENDED' },
        { label: 'Pending', value: 'PENDING' },
      ],
    },
    {
      name: 'internalAccountRoles',
      displayName: 'Account Role',
      type: 'CHECKLIST',
      options: [
        { label: 'DEFAULT', value: 'DEFAULT' },
        { label: 'SPONSOR', value: 'SPONSOR' },
        { label: 'ADJUSTMENT', value: 'ADJUSTMENT' },
        { label: 'SETTLEMENT', value: 'SETTLEMENT' },
        { label: 'FBO', value: 'FBO' },
        { label: 'SUSPENSE', value: 'SUSPENSE' },
        { label: 'ESCROW', value: 'ESCROW' },
      ],
    },
    {
      name: 'createdAt',
      displayName: 'Created Date',
      type: 'DATE',
      options: [
        { label: 'All Time', value: undefined },
        { label: 'Today', value: 'TODAY' },
        { label: 'Yesterday', value: 'YESTERDAY' },
        { label: 'This week', value: 'THIS_WEEK' },
        { label: 'This month', value: 'THIS_MONTH' },
        { label: 'Custom range', value: 'CUSTOM' },
      ],
      props: {
        maxMonths: '15',
      },
    },
    {
      name: 'updatedAt',
      displayName: 'Updated Date',
      type: 'DATE',
      options: [
        { label: 'All Time', value: undefined },
        { label: 'Today', value: 'TODAY' },
        { label: 'Yesterday', value: 'YESTERDAY' },
        { label: 'This week', value: 'THIS_WEEK' },
        { label: 'This month', value: 'THIS_MONTH' },
        { label: 'Custom range', value: 'CUSTOM' },
      ],
      props: {
        maxMonths: '15',
      },
    },
  ];

  sortParams: DatatableSorting = { key: 'createdAt', sortProp: 'createdAt', sortDir: 'desc' };

  get activePage(): number {
    const routePage = this.activatedRoute?.snapshot?.queryParams?.page;
    return routePage ? parseInt(routePage, 10) : 0;
  }

  financialAccounts$ = this.store.select(financialAccountFeature.selectFinancialAccounts);

  totalElements$ = this.store.select(financialAccountFeature.selectFinancialAccountsTotalElements);

  isLoading$ = this.store.select(financialAccountFeature.selectIsLoading);

  activeFilters$ = this.store.select(financialAccountFeature.selectFinancialAccountsActiveFilters);

  activeFiltersCount$ = this.store.select(financialAccountFeature.selectFinancialAccountsActiveFiltersCount);

  updateQueryParams(page?: RequestPageParams['page']): void {
    if (!isUndefined(page) && this.activePage !== page) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { page: page > 0 ? page : undefined },
        queryParamsHandling: 'merge',
      });
    }
  }

  getChildFinancialAccountList({ page, size, sortParams }: FetchData): void {
    if (sortParams) {
      this.sortParams = sortParams;
    }

    this.updateQueryParams(page);

    this.store.dispatch(
      FinancialAcccountActions.loadFinancialAccounts({
        params: {
          page: page || 0,
          size,
          searchString: this.searchString,
          sortParams: sortParams ?? this.sortParams,
          accountHolderTypes: ['CUSTOMER'],
          parentId: this.financialAccountId(),
        },
      }),
    );
  }

  onSearch(event: string): void {
    if (!event) {
      this.onSearchReset();
      return;
    }
    this.searchString = event;
    this.getChildFinancialAccountList({ page: 0 });
  }

  onSearchReset(): void {
    if (!this.searchString) {
      return;
    }
    this.searchString = undefined;
    this.getChildFinancialAccountList({ page: 0 });
  }

  applyFilters(filterValues: FilterValues): void {
    this.store.dispatch(FinancialAcccountActions.setFinancialAccountsActiveFilters({ filters: { ...pickBy(filterValues, identity) } }));
    this.getChildFinancialAccountList({ page: 0 });
  }
}
